import React from "react";
import { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { Button, Box, Typography } from "@mui/material";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { TextField } from '@mui/material';
import { Spinner } from "/@/app/components/Spinner";
import { freeMeasurementsViewState } from "/@/app/views/FreeMeasurementsView/FreeMeasurementsViewState";
import { ParameterAutocomplete } from "/@/app/views/FreeMeasurementsView/AddChangeParameter/ParameterAutocomplete";
import { useApi} from '/@/app/Api';
import { messageStackState } from "/@/app/states/messageStackState.js";


export const AddChangeParameter = observer(({ state, onDone }) => {
    const api=useApi();
    const [needParameterDetails, setNeedParameterDetails] = useState(!!state.records_id);
    const [loadingParameterDetails, setLoadingParameterDetails] = useState(false);

    const [parameterChoice, setParameterChoice] = useState({
        userDefined: state.records_id === null,
        value: state.records_id === null
            ? state.name
            : null  // will update when parameter details are loaded.
    });

    const [unitChoice, setUnitChoice] = useState(
        state.records_id === null
            ? {
                userDefined: true,
                value: state.unit,
            }
            : {
                userDefined: false,
                value: null  // will update when parameter details are loaded.
            }
    );

    // Update default units on parameter change
    useEffect(() => {
        if (state.records_id && parameterChoice?.value?.records_id === state.records_id) {
            // no parameter change, set selected unit
            const uList = parameterChoice.value.units.filter(u => u.records_id === state.unit_records_id);
            if (uList.length) {
                setUnitChoice({userDefined: false, value: uList[0]});
                return;
            }
            // continue, if selected unit not found
        }
        //
        // parameter choice changed
        //
        // 1. try to get default
        const recordsParameterPreset = parameterChoice?.value?.default_unit;
        if (recordsParameterPreset) {
            setUnitChoice({userDefined: false, value: recordsParameterPreset})
            return;
        }
        // 2. try to get 1st unit found
        const unitsAvailable = parameterChoice?.value?.units;
        if (unitsAvailable) {
            setUnitChoice({userDefined: false, value: unitsAvailable[0]})
            return;
        }
        // 3. ... fixme
        if (!parameterChoice.userDefined) {
            setUnitChoice({userDefined: false, value: state.unit})
        }
    }, [parameterChoice]);


    let convertable = {}
    if (parameterChoice?.value?.conversions) {
        convertable[parameterChoice.value.conversions.base_unit_id] = true;
        for (let id of Object.keys(parameterChoice.value.conversions.alternatives)) {
            convertable[id] = true;
        }
    }
    if (Object.keys(convertable).length < 2) {
        convertable = {};
    }

    // sc
    const [top, setTop] = useState(state.default_top_edge || "");
    const [bottom, setBottom] = useState(state.default_bottom_edge || "");

    const isSaving = state.saveStatus === "saving";

    const doSave = () => {
        if (isSaving) {
            return;
        }

        //
        // check values (draft)
        //
        if (
            // both or none
            ((top || bottom) && !(top && bottom))
            // bad range
            || (top && bottom && parseFloat(top) < parseFloat(bottom))
        ) {
            messageStackState.addMessage({
                text: 'Введите верные границы нормы.',
                tags: 'error',
                autoHide: 3000,
            });
            return;
        }
        if (parameterChoice.userDefined && !parameterChoice.value?.trim()) {
            messageStackState.addMessage({
                text: 'Введите название параметра.',
                tags: 'error',
                autoHide: 3000,
            });
            return;
        }

        //
        // Find related sections
        //
        let sections = [];
        if (state.sections !== null) {
            sections = state.sections.map((s) => s.id);
        }

        // plus current visible
        sections.push(freeMeasurementsViewState.activeSectionState.id);

        let payload;
        if (parameterChoice.userDefined) {
            payload = {
                // visible in this dialog:
                "name": parameterChoice.value,  // simple text
                "unit": unitChoice ? unitChoice.value : "",  // simple text
                "default_top_edge": top !== "" ? top : null,
                "default_bottom_edge": bottom !== "" ? bottom : null,
                "records_id": null,
                "unit_records_id": null,
                // invisible / assumed:
                "sections": sections,
            }
        } else {
            payload = {
                // visible in this dialog:
                "name": "",  // irrelevant when records_id set
                "unit": "",  // irrelevant
                // FIXME: range should become irrelevant in future
                "default_top_edge": top !== "" ? top : null,
                "default_bottom_edge": bottom !== "" ? bottom : null,
                "records_id": parameterChoice.value.records_id,
                "unit_records_id": unitChoice?.value.records_id,
                // invisible / assumed:
                "sections": sections,
            }
        }

        state.save(payload).then(() => {
            onDone();
        });
    }

    if (needParameterDetails) {
        if (loadingParameterDetails) {
            return;  // prevent double-render
        }
        setLoadingParameterDetails(true)
        api.request(
            `core/parameters/${state.records_id}/`, {},
            {
                method: "GET",
            }
        ).then((p) => {
            setParameterChoice({userDefined: false, value: p});
            if (p.units && state.unit_records_id) {
                const uList = p.units.filter(u => u.id === state.unit_records_id);
                if (uList.length) {
                    setUnitChoice({userDefined: false, value: uList[0]});
                } else {
                    setUnitChoice({userDefined: false, value: null});
                }
            }
            setNeedParameterDetails(false);
        }).catch(error => {
            onDone();  // close on error
        }).finally(() => {
            setLoadingParameterDetails(false);
        });

        return (
            <Box sx={{ textAlign: "center" }}>
                <Spinner size={60} delay={300}></Spinner>
            </Box>
        )
    }

    let bugUnitNotFound = true;
    if (unitChoice?.value?.records_id) {
        if (!parameterChoice?.value?.units) {
            // pass; can be undefined when changing texts.
        } else {
            const options = parameterChoice.value.units.map((unit, index) => unit.records_id);
            bugUnitNotFound = options.indexOf(unitChoice.value.records_id) === -1;
        }
    }

    return (
        <>
            <Typography variant="h6" component="h2">
                {state.id === null
                    ? <>Добавить параметр</>
                    : <>Изменить параметр</>
                }
            </Typography>

            <Box sx={{ mt: 2, display: "flex", flexFlow: "column nowrap", gap: 1, alignItems: "center" }}>
                <ParameterAutocomplete
                    parameterChoice={parameterChoice}  // initial value
                    setParameterChoice={setParameterChoice}
                    disabled={state.panel_bound}
                    sx={{ width: "100%" }}
                />
                {(parameterChoice === null || parameterChoice.userDefined)
                    ? <TextField
                        label="единицы измерения"
                        variant="outlined"
                        value={(unitChoice && unitChoice.userDefined) ? (unitChoice.value || "") : ""}
                        onChange={
                            e => setUnitChoice({
                                userDefined: true,
                                value: e.target.value,
                            })
                        }
                        sx={{ input: { textAlign: 'left' } }}
                    />
                    :
                    <>
                        {/* XXX hide for now if unit not found. */}
                        {/* TODO fix later with better Records integration. */}
                        {!bugUnitNotFound &&
                            <Select
                                value={unitChoice?.value?.records_id || ""}
                                label="Единицы измерения"
                                onChange={
                                    e => {
                                        setUnitChoice({
                                            userDefined: false,
                                            value: parameterChoice.value.units.filter((a) => a.records_id === e.target.value)[0] || null,
                                        });
                                    }
                                }
                            >
                                {parameterChoice.value.units.map((unit, index) => (
                                    <MenuItem
                                        key={unit.records_id}
                                        value={unit.records_id}
                                    >
                                        {unit.records_id in convertable
                                            ? <>
                                                <b>{unit.short_ru}</b>
                                            </>
                                            : <>{unit.short_ru}</>
                                        }

                                    </MenuItem>
                                ))}
                            </Select>
                        }
                    </>
                }

                <TextField
                    label="Верхняя граница нормы"
                    variant="standard"
                    value={top}
                    onChange={e => setTop(e.target.value)}
                    sx={{
                        input: { textAlign: 'center' },
                        minWidth: "250px",
                    }}
                />
                <TextField
                    label="Нижняя граница нормы"
                    variant="standard"
                    value={bottom}
                    onChange={e => setBottom(e.target.value)}
                    sx={{
                        input: { textAlign: 'center' },
                        minWidth: "250px",
                    }}
                />

                <Button
                    variant="contained"
                    color={
                        parameterChoice?.userDefined ? "warning" : "primary"
                    }
                    onClick={doSave}
                    disabled={isSaving || !parameterChoice}
                    sx={{ mt: 2 }}
                >
                    {isSaving
                        ? <Spinner size={30} sx={{ color: "white" }}/>
                        : (
                            (
                                state.id
                                    ? "Сохранить "
                                    : "Создать "
                            ) +
                            (
                                parameterChoice?.userDefined
                                    ? "персональный параметр"
                                    : "параметр"
                            )
                        )
                    }
                </Button>
                <Box sx={{ height: "auto", fontSize: "14px" }}>
                    {
                        parameterChoice?.userDefined &&
                        <Typography
                            sx={{ fontSize: "14px", textAlign: "center" }}
                            color={(t) => t.palette.warning.main}
                        >
                            По возможности, выберите параметр из списка подсказок.
                            Персональные параметры не привязываются к существующим лабораторным данным.
                        </Typography>
                    }
                </Box>
            </Box>
        </>
    );
});
